export type PageType =
  | 'home'
  | 'service_areas_index'
  | 'service_area'
  | 'service_neighbourhood'
  | 'product'
  | 'landing_page'
  | 'blog_index'
  | 'blog_post'
  | 'local_watch_index'
  | 'local_watch_city'
  | 'local_watch_post'
  | 'faq'
  | 'about'
  | 'warranty'
  | 'press'
  | 'privacy'
  | 'terms'
  | 'other'

const STATIC_PAGE_TYPES = {
  '/faq': 'faq',
  '/about': 'about',
  '/warranty': 'warranty',
  '/press': 'press',
  '/privacy': 'privacy',
  '/terms': 'terms',
} as const satisfies Record<string, PageType>

export type PageContext = {
  page_type: PageType
  service_city?: string
  service_neighbourhood?: string
  product_slug?: string
  /** Ad-group slug behind a `/lp/*` paid landing page. */
  lp_slug?: string
  blog_slug?: string
  local_watch_city?: string
  local_watch_slug?: string
}

export function deriveContextFromPath(pathname: string): PageContext {
  if (pathname === '/') return { page_type: 'home' }

  const staticType = STATIC_PAGE_TYPES[pathname as keyof typeof STATIC_PAGE_TYPES]
  if (staticType) return { page_type: staticType }

  if (pathname === '/service-areas') return { page_type: 'service_areas_index' }

  const neighbourhoodMatch = pathname.match(/^\/service-areas\/([^/]+)\/([^/]+)$/)
  if (neighbourhoodMatch) {
    return {
      page_type: 'service_neighbourhood',
      service_city: neighbourhoodMatch[1],
      service_neighbourhood: neighbourhoodMatch[2],
    }
  }

  const cityMatch = pathname.match(/^\/service-areas\/([^/]+)$/)
  if (cityMatch) {
    return { page_type: 'service_area', service_city: cityMatch[1] }
  }

  // Paid landing pages. Without this branch every `/lp/*` event reported
  // `page_type: 'other'`, which lumps the pages we buy traffic to in with
  // miscellany and makes paid performance unsegmentable in GA4.
  const landingMatch = pathname.match(/^\/lp\/([^/]+)$/)
  if (landingMatch) {
    return { page_type: 'landing_page', lp_slug: landingMatch[1] }
  }

  const productMatch = pathname.match(/^\/products\/([^/]+)$/)
  if (productMatch) {
    return { page_type: 'product', product_slug: productMatch[1] }
  }

  if (pathname === '/blog') return { page_type: 'blog_index' }
  const blogMatch = pathname.match(/^\/blog\/([^/]+)$/)
  if (blogMatch) {
    return { page_type: 'blog_post', blog_slug: blogMatch[1] }
  }

  if (pathname === '/local-watch') return { page_type: 'local_watch_index' }
  const localWatchPostMatch = pathname.match(/^\/local-watch\/([^/]+)\/([^/]+)$/)
  if (localWatchPostMatch) {
    return {
      page_type: 'local_watch_post',
      local_watch_city: localWatchPostMatch[1],
      local_watch_slug: localWatchPostMatch[2],
    }
  }
  const localWatchCityMatch = pathname.match(/^\/local-watch\/([^/]+)$/)
  if (localWatchCityMatch) {
    return {
      page_type: 'local_watch_city',
      local_watch_city: localWatchCityMatch[1],
    }
  }

  return { page_type: 'other' }
}
